/**
 * HelpText — the shared renderer for help-registry copy (Help v2, 2026-07-23).
 *
 * Turns the plain strings in helpContent.tsx into live text:
 *   1. `[Label](help-key)` markdown cross-links become real links. Before
 *      this, the brackets rendered literally in the modal/rail ("Use the
 *      [Project List](project-list)…") — Connor caught it on the Project
 *      Detail screenshots. A link navigates to the target page via
 *      HELP_ROUTES and drops into its Explore overlay when it has a tour;
 *      unmapped keys fall back to the Help Center pre-searched for the key.
 *   2. Glossary terms (CPI, SPI, baseline, …) get a dotted underline and a
 *      hover/focus definition card. Definitions live once in HELP_GLOSSARY
 *      and appear on every page whose copy uses the term — zero per-page
 *      authoring. Only the first occurrence per rendered block is marked so
 *      tip lists don't turn into a wall of dots.
 *
 * Every help surface (HowThisWorks modal, PageExploreOverlay rail, Help
 * Center) should render registry copy through this component, never as a
 * bare string.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { HELP_ROUTES } from '@/components/help/helpRoutes'
import { hasPageTour } from './pageTour/pageTours'
import { usePageTourStore } from './pageTour/pageTourStore'

// ─── Shared glossary ─────────────────────────────────────────────────────────
// Written once, hover-defined everywhere. Acronyms match case-sensitively
// (so "spi" inside a word never triggers); phrases match case-insensitively
// on word boundaries.
export interface GlossaryEntry {
  /** Canonical display term (used as the card heading). */
  title: string
  /** One- or two-sentence plain-English definition. */
  def: string
  /** Matcher — must NOT be global (we take the first match per scan). */
  pattern: RegExp
}

export const HELP_GLOSSARY: GlossaryEntry[] = [
  {
    title: 'CPI — Cost Performance Index',
    def: 'Earned value ÷ actual cost. Above 1.0 means the project is getting more work per dollar than planned; below 1.0 means over-spending for the work done.',
    pattern: /\bCPI\b/,
  },
  {
    title: 'SPI — Schedule Performance Index',
    def: 'Earned value ÷ planned value. Below 1.0 means behind schedule; above 1.0 means ahead. Trends matter more than a single reading.',
    pattern: /\bSPI\b/,
  },
  {
    title: 'EVM — Earned Value Management',
    def: 'The cost-and-schedule measurement framework. Pages read the latest stored EVM snapshot, which refreshes when actuals post — numbers step rather than tick live.',
    pattern: /\bEVM\b/,
  },
  {
    title: 'Baseline',
    def: 'The approved, locked plan (dates, budget) that progress is measured against. Changing it requires a formal re-baseline in Baseline Management.',
    pattern: /\bbaselines?\b/i,
  },
  {
    title: 'WBS — Work Breakdown Structure',
    def: 'The hierarchical decomposition of project scope that cost and schedule roll up through. Charge codes and work authorizations hang off WBS elements.',
    pattern: /\bWBS\b/,
  },
  {
    title: 'DCAA',
    def: 'Defense Contract Audit Agency — the auditor for government contract costs. Its timekeeping rules drive daily entry, edit reasons, and the immutable audit trail.',
    pattern: /\bDCAA\b/,
  },
  {
    title: 'Attestation',
    def: 'The typed-name certification a worker signs when submitting time, recorded immutably per charged day. Required for compliant timekeeping — submitting IS the sign-off.',
    pattern: /\battestations?\b/i,
  },
  {
    title: 'Charge line',
    def: 'Pairs you with a specific charge code (contract, WBS, task) for the week. Hours are always entered against a charge line, never free-floating.',
    pattern: /\bcharge lines?\b/i,
  },
  {
    title: 'Period of performance',
    def: 'The contractual date window in which work may be charged. Days outside a charge line\'s period of performance are blocked for entry.',
    pattern: /\bperiods? of performance\b/i,
  },
  {
    title: 'Management Reserve (MR)',
    def: 'Budget held back from the performance baseline for unknown-unknowns. Part of the identity CBB = PMB + MR.',
    pattern: /\bManagement Reserve\b/i,
  },
]

// ─── Parsing ─────────────────────────────────────────────────────────────────
type Seg =
  | { kind: 'text'; text: string }
  | { kind: 'link'; label: string; helpKey: string }
  | { kind: 'gloss'; text: string; entry: GlossaryEntry }

const LINK_RE = /\[([^\]]+)\]\(([a-z0-9][a-z0-9-]*)\)/g

/** Split one plain-text chunk into text + first-occurrence glossary segments. */
function glossSegments(text: string, seen: Set<GlossaryEntry>): Seg[] {
  const out: Seg[] = []
  let rest = text
  // Scan repeatedly: each pass finds the earliest unseen-term match.
  for (;;) {
    let best: { entry: GlossaryEntry; index: number; match: string } | null = null
    for (const entry of HELP_GLOSSARY) {
      if (seen.has(entry)) continue
      const m = entry.pattern.exec(rest)
      if (m && (best === null || m.index < best.index)) {
        best = { entry, index: m.index, match: m[0] }
      }
    }
    if (!best) break
    if (best.index > 0) out.push({ kind: 'text', text: rest.slice(0, best.index) })
    out.push({ kind: 'gloss', text: best.match, entry: best.entry })
    seen.add(best.entry)
    rest = rest.slice(best.index + best.match.length)
  }
  if (rest) out.push({ kind: 'text', text: rest })
  return out
}

/** Exported for tests: full segmentation of a help copy string. */
export function segmentHelpText(text: string): Seg[] {
  const segs: Seg[] = []
  const seen = new Set<GlossaryEntry>()
  let last = 0
  LINK_RE.lastIndex = 0
  for (let m = LINK_RE.exec(text); m; m = LINK_RE.exec(text)) {
    if (m.index > last) segs.push(...glossSegments(text.slice(last, m.index), seen))
    segs.push({ kind: 'link', label: m[1]!, helpKey: m[2]! })
    last = m.index + m[0].length
  }
  if (last < text.length) segs.push(...glossSegments(text.slice(last), seen))
  return segs
}

// ─── Glossary hover card ─────────────────────────────────────────────────────
function GlossTerm({ text, entry }: { text: string; entry: GlossaryEntry }) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative inline-block">
      <span
        tabIndex={0}
        role="term"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="cursor-help border-b-2 border-dotted border-brand-400 font-medium text-inherit"
      >
        {text}
      </span>
      {open && (
        <span
          role="tooltip"
          className="absolute left-0 top-full z-[70] mt-1.5 block w-60 rounded-lg bg-slate-800 px-3 py-2.5 text-[11px] font-normal leading-snug text-white shadow-xl ring-1 ring-black/20"
        >
          <span className="mb-0.5 block text-[11.5px] font-semibold">{entry.title}</span>
          {entry.def}
          <span className="mt-1.5 block text-[9.5px] uppercase tracking-wider text-white/50">Shared glossary</span>
        </span>
      )}
    </span>
  )
}

// ─── The renderer ────────────────────────────────────────────────────────────
export function HelpText({
  text,
  className,
  onNavigate,
}: {
  text: string
  className?: string
  /** Called before a cross-link navigates — close the modal / exit Explore. */
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  const startExplore = usePageTourStore((s) => s.startExplore)
  const segs = useMemo(() => segmentHelpText(text), [text])

  const follow = (helpKey: string) => {
    onNavigate?.()
    const path = HELP_ROUTES[helpKey]
    if (path) {
      navigate(path)
      // Same pattern as the Help Center: let the destination mount, then
      // drop into its Explore overlay when it has a tour.
      if (hasPageTour(helpKey)) window.setTimeout(() => startExplore(helpKey), 450)
    } else {
      navigate(`/help?q=${encodeURIComponent(helpKey)}`)
    }
  }

  return (
    <span className={className}>
      {segs.map((s, i) => {
        if (s.kind === 'link') {
          return (
            <button
              key={i}
              type="button"
              onClick={() => follow(s.helpKey)}
              className="inline font-semibold text-brand-600 underline decoration-brand-300 decoration-dotted underline-offset-2 hover:text-brand-700 hover:decoration-solid"
            >
              {s.label}
            </button>
          )
        }
        if (s.kind === 'gloss') return <GlossTerm key={i} text={s.text} entry={s.entry} />
        return <span key={i}>{s.text}</span>
      })}
    </span>
  )
}
